import { supabase } from '../config/supabase';
import { AgendaEventIntegrated } from '../types/integration'; // Manter, já atualizado
import { ProfessionalPaymentReport, ProfessionalPaymentSummary, ProfessionalConfig, PeriodType, AppointmentDetail } from '../types/professional-payment';
import { IntegrationService } from './IntegrationService'; // Manter, já atualizado
import { DoctorPricingRuleService } from './DoctorPricingRuleService';
import { normalizeAppointmentLocation } from '../utils/appointmentLocation';
import { format } from 'date-fns';

const relationRows = <T,>(value: T | T[] | null | undefined): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

interface FirebaseUser { // Definição temporária para FirebaseUser
  uid: string;
  doctorId?: string;
  displayName: string;
  userAssignedCalendarName?: string;
  clientId?: string;
  isFinanciallyActive?: boolean;
  // ... outros campos necessários
}

export class ProfessionalPaymentService {
  private static async filterAlreadyPaidAppointments(
    appointments: AgendaEventIntegrated[],
    clientId?: string,
    doctorId?: string
  ): Promise<AgendaEventIntegrated[]> {
    if (!clientId || !doctorId) {
      return appointments;
    }

    const appointmentIds = appointments
      .map((appointment) => appointment.id)
      .filter((appointmentId): appointmentId is string => Boolean(appointmentId));

    if (appointmentIds.length === 0) {
      return appointments;
    }

    const { data, error } = await supabase
      .from('professional_payment_appointments')
      .select('appointmentId')
      .eq('clientId', clientId)
      .eq('doctorId', doctorId)
      .in('appointmentId', appointmentIds);

    if (error) {
      throw error;
    }

    const paidAppointmentIds = new Set((data || []).map((row: { appointmentId: string }) => row.appointmentId));
    return appointments.filter((appointment) => !paidAppointmentIds.has(appointment.id));
  }

  static async getFisioterapeutas(clientId?: string): Promise<FirebaseUser[]> {
    try {
      let query = supabase
        .from("users")
        .select(`
          userId,
          userDisplayName,
          userAssignedCalendarName,
          user_clients!inner ( userRole, clientId ),
          doctors!left ( doctorId, userId )
        `)
        .eq("user_clients.userRole", "fisioterapeuta");

      if (clientId) {
        query = query.eq("user_clients.clientId", clientId);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Erro ao buscar fisioterapeutas do Supabase:", error);
        throw error;
      }

      const activeProfessionalIds = new Set<string>();
      if (clientId) {
        const today = format(new Date(), 'yyyy-MM-dd');
        const { data: roles, error: rolesError } = await supabase
          .from('professional_financial_roles')
          .select('professionalId')
          .eq('clientId', clientId)
          .eq('financialRoleType', 'fisioterapeuta')
          .eq('compensationModel', 'por_sessao')
          .eq('roleStatus', 'active')
          .lte('effectiveFrom', today)
          .or(`effectiveUntil.is.null,effectiveUntil.gte.${today}`);

        if (rolesError) throw rolesError;
        (roles || []).forEach((role: { professionalId: string }) => activeProfessionalIds.add(role.professionalId));
      }

      const fisioterapeutas = data.map((user: any) => {
        const userClientRows = relationRows(user.user_clients);
        const doctorRows = relationRows(user.doctors);
        const resolvedClientId = clientId || userClientRows[0]?.clientId;
        const resolvedDoctorId = doctorRows[0]?.doctorId;

        return {
          uid: user.userId,
          doctorId: resolvedDoctorId,
          displayName: user.userDisplayName,
          userAssignedCalendarName: user.userAssignedCalendarName,
          clientId: resolvedClientId,
          isFinanciallyActive: Boolean(resolvedDoctorId && activeProfessionalIds.has(resolvedDoctorId)),
        };
      });

      const uniqueByUid = new Map<string, FirebaseUser>();
      for (const fisioterapeuta of fisioterapeutas) {
        if (!uniqueByUid.has(fisioterapeuta.uid)) {
          uniqueByUid.set(fisioterapeuta.uid, fisioterapeuta);
        }
      }

      return Array.from(uniqueByUid.values()) as FirebaseUser[];
    } catch (error) {
      console.error('Error fetching fisioterapeutas:', error);
      throw error;
    }
  }

  static async getProfessionalsForFinancialRoles(clientId: string): Promise<FirebaseUser[]> {
    const { data, error } = await supabase
      .from('users')
      .select(`
        userId,
        userDisplayName,
        userAssignedCalendarName,
        user_clients!inner ( userRole, clientId ),
        doctors!inner ( doctorId, userId )
      `)
      .eq('user_clients.clientId', clientId);

    if (error) throw error;

    const uniqueByDoctorId = new Map<string, FirebaseUser>();
    for (const user of data || []) {
      const userClientRows = relationRows((user as any).user_clients);
      const doctorRows = relationRows((user as any).doctors);
      const doctorId = doctorRows[0]?.doctorId;
      if (!doctorId || !userClientRows.some((row: any) => row.clientId === clientId)) continue;
      uniqueByDoctorId.set(doctorId, {
        uid: (user as any).userId,
        doctorId,
        displayName: (user as any).userDisplayName || 'Profissional sem nome',
        userAssignedCalendarName: (user as any).userAssignedCalendarName,
        clientId,
      });
    }
    return Array.from(uniqueByDoctorId.values());
  }

  static async getAppointmentsByCalendarFromMonthlyClosing(
    calendarName: string,
    month: number,
    year: number,
    clientId?: string
  ): Promise<AgendaEventIntegrated[]> {
    try {
      const monthString = `${year}-${String(month).padStart(2, '0')}`;
      console.log(`📊 Buscando appointments para calendário "${calendarName}" no fechamento mensal ${monthString}`);

      const monthlyClosing = await IntegrationService.generateMonthlyClosing(monthString, clientId);

      const allAppointments: AgendaEventIntegrated[] = [];

      for (const item of monthlyClosing.items) {
        // O filtro por calendar_name deve ser feito no appointmentRow.users?.userAssignedCalendarName
        // já que agora temos o JOIN na IntegrationService.getEnrichedAppointments
        for (const appointment of item.appointments) {
          if (appointment.calendar_name === calendarName) { // Este calendar_name agora vem do JOIN com users
            allAppointments.push(appointment);
          }
        }
      }

      console.log(`✅ Encontrados ${allAppointments.length} appointments para o calendário "${calendarName}"`);

      return allAppointments;
    } catch (error) {
      console.error('Error fetching appointments from monthly closing:', error);
      throw error;
    }
  }

  static async getAppointmentsByCalendarAndDateRange(
    calendarName: string,
    startDate: Date,
    endDate: Date,
    clientId?: string
  ): Promise<AgendaEventIntegrated[]> {
    try {
      console.log(`📊 Buscando appointments para calendário "${calendarName}" entre ${format(startDate, 'dd/MM/yyyy HH:mm:ss')} e ${format(endDate, 'dd/MM/yyyy HH:mm:ss')} (inclusive)`);

      let appointmentsQuery = supabase
        .from("appointments")
        .select(`
          *,
          appointmentPaymentOrigin,
          users!appointments_userId_fkey ( userAssignedCalendarName )
        `)
        .eq("appointmentStatus", "completed") // Apenas agendamentos concluídos
        .gte("appointmentStartDate", startDate.toISOString())
        .lte("appointmentStartDate", endDate.toISOString());

      if (clientId) {
        appointmentsQuery = appointmentsQuery.eq("clientId", clientId);
      }

      const { data: appointmentsData, error } = await appointmentsQuery;

      if (error) {
        console.error("Erro ao buscar agendamentos do Supabase por range de datas:", error);
        throw error;
      }

      const completedEvents: AgendaEventIntegrated[] = [];

      for (const appointmentRow of appointmentsData) {
        if (appointmentRow.users?.userAssignedCalendarName === calendarName) {
          const startDateTime = new Date(appointmentRow.appointmentStartDate);
          const endDateTime = appointmentRow.appointmentEndDate ? new Date(appointmentRow.appointmentEndDate) : startDateTime;

          completedEvents.push({
            id: appointmentRow.appointmentId,
            title: appointmentRow.appointmentTitle || "",
            subject: appointmentRow.appointmentTitle, // Mapeando title para subject
            start: startDateTime,
            end: endDateTime,
            calendar_name: appointmentRow.users?.userAssignedCalendarName || "",
            location: appointmentRow.appointmentLocation || undefined,
            patientId: appointmentRow.patientId || undefined,
            patientName: appointmentRow.patients?.patientFullName || undefined, // patientFullName virá do IntegrationService.getEnrichedAppointments se for usado diretamente
            status: appointmentRow.appointmentStatus,
            appointmentStatus: {
              hasEvolution: !!appointmentRow.appointmentEvolutionId,
              evolutionId: appointmentRow.appointmentEvolutionId || undefined,
              isBilled: appointmentRow.appointmentIsBilled || false,
              invoiceId: appointmentRow.invoiceId || undefined,
              paymentOrigin: appointmentRow.appointmentPaymentOrigin || undefined
            },
            absenceInfo: appointmentRow.appointmentAbsenceInfo || undefined
          });
        }
      }

      console.log(`✅ Encontrados ${completedEvents.length} appointments concluídos para o calendário "${calendarName}"`);

      return completedEvents;
    } catch (error) {
      console.error('Error fetching appointments by date range:', error);
      throw error;
    }
  }

  static async generatePaymentReport(
    professional: FirebaseUser,
    month: number,
    year: number,
    periodType: PeriodType = 'billing_period',
    customStartDate?: Date,
    customEndDate?: Date
  ): Promise<ProfessionalPaymentReport | null> {
    // A lógica de userAssignedCalendarName agora está dentro de getFisioterapeutas
    // mas podemos manter essa validação para garantir que o objeto professional esteja completo
    if (!professional.userAssignedCalendarName) {
      console.log(`⚠️ Profissional ${professional.displayName} não tem calendário atribuído`);
      return null;
    }

    try {
      console.log(`📋 Gerando relatório para ${professional.displayName} (${professional.userAssignedCalendarName}) - Tipo: ${periodType}`);

      let appointments: AgendaEventIntegrated[];

      if (periodType === 'date_range' && customStartDate && customEndDate) {
        appointments = await this.getAppointmentsByCalendarAndDateRange(
          professional.userAssignedCalendarName, // Usar userAssignedCalendarName do objeto professional
          customStartDate,
          customEndDate,
          professional.clientId
        );
      } else {
        appointments = await this.getAppointmentsByCalendarFromMonthlyClosing(
          professional.userAssignedCalendarName, // Usar userAssignedCalendarName do objeto professional
          month,
          year,
          professional.clientId
        );
      }

      const completedAppointments = appointments.filter(
        app => app.status === 'completed' // Verificar o campo 'status' do agendamento
      );

      const pendingCompletedAppointments = await this.filterAlreadyPaidAppointments(
        completedAppointments,
        professional.clientId,
        professional.doctorId
      );

      if (pendingCompletedAppointments.length === 0) {
        return null;
      }

      const withEvolution = pendingCompletedAppointments.filter(
        app => app.appointmentStatus?.evolutionId // Verificar evolutionId direto
      ).length;

      const pricingRules = professional.clientId && professional.doctorId
        ? await DoctorPricingRuleService.getRulesForDoctor(professional.clientId, professional.doctorId)
        : [];
      const pricingByKey = new Map(
        pricingRules.map((rule) => [
          `${rule.pricingRuleAppointmentLocation}::${rule.pricingRuleAppointmentPaymentOrigin}`,
          Number(rule.pricingRulePricePerSession || 0),
        ])
      );

      const appointmentPricingById: Record<string, number> = {};
      let hasMissingPricingRule = false;
      const totalAmount = pendingCompletedAppointments.reduce((sum, app) => {
        const normalizedLocation = normalizeAppointmentLocation(app.location);
        const paymentOrigin = app.appointmentStatus?.paymentOrigin;
        const ruleKey = normalizedLocation && paymentOrigin ? `${normalizedLocation}::${paymentOrigin}` : '';
        const hasRule = ruleKey ? pricingByKey.has(ruleKey) : false;
        const sessionPrice = hasRule ? (pricingByKey.get(ruleKey) ?? 0) : 0;
        if (!hasRule) {
          hasMissingPricingRule = true;
        }
        if (app.id) {
          appointmentPricingById[app.id] = sessionPrice;
        }
        return sum + sessionPrice;
      }, 0);

      const pricePerSession = pendingCompletedAppointments.length > 0
        ? totalAmount / pendingCompletedAppointments.length
        : 0;

      console.log(`✅ Relatório gerado: ${pendingCompletedAppointments.length} sessões concluídas (${withEvolution} com evolução)`);

      return {
        professionalId: professional.uid,
        professionalDoctorId: professional.doctorId,
        professionalName: professional.displayName,
        calendarName: professional.userAssignedCalendarName,
        period: { month, year },
        sessions: {
          total: appointments.length,
          completed: pendingCompletedAppointments.length,
          withEvolution,
          withoutEvolution: pendingCompletedAppointments.length - withEvolution
        },
        payment: {
          pricePerSession,
          totalAmount,
          isPaid: false
        },
        appointments: pendingCompletedAppointments,
        appointmentPricingById,
        hasMissingPricingRule
      };
    } catch (error) {
      console.error('Error generating payment report:', error);
      throw error;
    }
  }

  static convertAppointmentsToDetails(
    appointments: AgendaEventIntegrated[],
    pricePerSession: number,
    appointmentPricingById?: Record<string, number>
  ): AppointmentDetail[] {
    return appointments.map(app => {
      let eventDate = '';
      let eventTime = '';
      let startDateObj: Date | null = null;

      // Assumimos que app.start já é um objeto Date
      if (app.start instanceof Date) {
        startDateObj = app.start;
      } else if (typeof app.start === 'string') { // Caso ainda venha como string ISO
        startDateObj = new Date(app.start);
      } else {
        startDateObj = new Date(); // Fallback para evitar erro, mas deve ser tratado upstream
      }

      // Extrair data e hora separadamente
      if (startDateObj && !isNaN(startDateObj.getTime())) {
        eventDate = format(startDateObj, 'dd/MM/yyyy');
        eventTime = format(startDateObj, 'HH:mm');
      }

      return {
        id: app.id || '',
        date: eventDate,
        time: eventTime,
        patientName: app.patientName || app.title || 'Paciente não identificado', // Usar patientName do Supabase
        description: app.title || '', // Usar title
        location: app.location || '',
        paymentOrigin: app.appointmentStatus?.paymentOrigin || '',
        hasEvolution: !!(app.appointmentStatus?.evolutionId), // Verificar apenas evolutionId
        evolutionId: app.appointmentStatus?.evolutionId,
        isBilled: !!(app.appointmentStatus?.isBilled),
        status: app.status || 'completed',
        value: appointmentPricingById?.[app.id || ''] ?? pricePerSession
      };
    });
  }

  static async generateAllReports(
    month: number,
    year: number,
    periodType: PeriodType = 'billing_period',
    customStartDate?: Date,
    customEndDate?: Date,
    clientId?: string
  ): Promise<ProfessionalPaymentReport[]> {
    try {
      const fisioterapeutas = await this.getFisioterapeutas(clientId);

      const activeWithCalendar = fisioterapeutas.filter(
        prof => prof.isFinanciallyActive && prof.userAssignedCalendarName
      );

      const reports = await Promise.all(
        activeWithCalendar.map(prof =>
          this.generatePaymentReport(prof, month, year, periodType, customStartDate, customEndDate)
        )
      );

      return reports.filter((report): report is ProfessionalPaymentReport => report !== null);
    } catch (error) {
      console.error('Error generating all reports:', error);
      throw error;
    }
  }

  static exportReportToCSV(
    report: ProfessionalPaymentReport,
    periodType: PeriodType,
    customPeriod?: string
  ): string {
    const appointmentDetails = this.convertAppointmentsToDetails(
      report.appointments,
      report.payment.pricePerSession,
      report.appointmentPricingById
    );

    const headers = [
      "Profissional",
      "Período",
      "Tipo de Período",
      "Data",
      "Horário",
      "Paciente",
      "Local",
      "Evolução",
      "Fatura",
      "Valor por Sessão"
    ];

    const periodLabel = periodType === "date_range" && customPeriod
      ? customPeriod
      : `${String(report.period.month).padStart(2, "0")}/${report.period.year}`;

    const periodTypeLabel = periodType === "date_range" ? "Data Fixa" : "Faturamento";

    const rows = appointmentDetails.map(detail => [
      report.professionalName,
      periodLabel,
      periodTypeLabel,
      detail.date,
      detail.time,
      detail.patientName,
      detail.location,
      detail.hasEvolution ? "Sim" : "Não",
      detail.isBilled ? "Sim" : "Não",
      `R$ ${detail.value.toFixed(2)}`
    ]);

    const totalRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "",
      "TOTAL:",
      `${report.sessions.completed} sessões - R$ ${report.payment.totalAmount.toFixed(2)}`
    ];

    const summaryRows = [
      [],
      ["Resumo:"],
      ["Total de Sessões:", report.sessions.completed.toString()],
      ["Sessões com Evolução:", report.sessions.withEvolution.toString()],
      ["Sessões sem Evolução:", report.sessions.withoutEvolution.toString()],
      ["Valor Total:", `R$ ${report.payment.totalAmount.toFixed(2)}`]
    ];

    const allRows = [headers, ...rows, totalRow, ...summaryRows];

    return allRows.map(row =>
      row.map(cell => {
        const cellStr = String(cell || "");
        if (cellStr.includes(",") || cellStr.includes("\"") || cellStr.includes("\n")) {
          // Corrigindo o escape de aspas para CSV
          return `"${cellStr.replace(new RegExp("\"", "g"), "\"\"")}"`;
        }
        return cellStr;
      }).join(",")
    ).join("\n");
  }

  static calculateSummary(reports: ProfessionalPaymentReport[]): ProfessionalPaymentSummary {
    return {
      totalProfessionals: reports.length,
      totalSessions: reports.reduce((sum, r) => sum + r.sessions.completed, 0),
      totalAmount: reports.reduce((sum, r) => sum + r.payment.totalAmount, 0),
      activeProfessionals: reports.filter(r => r.sessions.completed > 0).length
    };
  }

  static async getProfessionalConfigs(clientId?: string): Promise<ProfessionalConfig[]> {
    try {
      const fisioterapeutas = await this.getFisioterapeutas(clientId);

      return fisioterapeutas.map(prof => ({
        uid: prof.uid,
        displayName: prof.displayName,
        calendarName: prof.userAssignedCalendarName,
        isActive: prof.isFinanciallyActive ?? false,
      }));
    } catch (error) {
      console.error('Error fetching professional configs:', error);
      throw error;
    }
  }
}
